import clsx from 'clsx';
import { Formik } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

import { PrimaryButton } from '@components/Button/variants';
import ConfirmActionViaEmailCode from '@components/ConfirmActionViaEmailCode';
import Divider from '@components/Divider';
import RadioButtonGroup from '@components/Form/RadioButtonGroup';
import TextAreaField from '@components/Form/TextAreaField';
import TokenizeTextField from '@components/Form/TokenizeTextField';
import PriceBreakDown from '@components/pages/Settings/SettingsPage/Team/CostSummary';
import { useGiftSubscriptionCreate, useGroupSubscriptionConfirm } from '@hooks';
import { GROUP_SUBSCRIPTION_GIFT } from '@utils/constants';
import { AutocompleteOption } from '@utils/interfaces';

import schema from './schema';
import styles from './styles.module.scss';

const AUTO_RENEW_OPTIONS = [
  { label: 'Ongoing subscription', value: 'ongoing_subscription' },
  { label: 'One year only', value: 'one_year_only' },
];

export interface FormValues {
  emails: AutocompleteOption[];
  notes: string;
  autoRenew: string;
}

const INITIAL_STATE: FormValues = {
  emails: [],
  notes: '',
  autoRenew: AUTO_RENEW_OPTIONS[0].value,
};

const GiftForm = ({
  forGiftLandingPage,
  setCurrentFormStep,
  setNumberOfGifts,
}: {
  forGiftLandingPage?: boolean;
  setCurrentFormStep?: (state: number) => void;
  setNumberOfGifts?: (state: number) => void;
}) => {
  const { mutateAsync: createGiftSubscription, isLoading: isLoadingPurchase } =
    useGiftSubscriptionCreate(forGiftLandingPage);
  const { mutateAsync: confirmGroupSubscription, isLoading: isLoadingForPriceConfirmation } =
    useGroupSubscriptionConfirm();
  const [totalCostWithTax, setTotalCostWithTax] = useState<number>(0);
  const [totalCostWithoutTax, setTotalCostWithoutTax] = useState<number>(0);
  const [totalTaxAmount, setTotalTaxAmount] = useState<number>(0);
  const [totalEligibleSubs, setTotalEligibleSubs] = useState<number>(0);
  const [isUserConfirmed, setIsUserConfirmed] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

  const pendingSubmitRef = useRef<null | (() => void)>(null);

  useEffect(() => {
    if (isUserConfirmed && pendingSubmitRef.current) {
      pendingSubmitRef.current();
      pendingSubmitRef.current = null; // prevent double-submit
    }
  }, [isUserConfirmed]);

  const confirmSubmit = async (values, formik) => {
    if (isLoadingPurchase) {
      return;
    }
    pendingSubmitRef.current = () => handleSubmit(values, formik);
    setShowConfirmationModal(true);
  };

  const handleSubmit = async ({ emails, notes, autoRenew }, formik) => {
    const sanitizedParams = {
      emails: emails.map((email: AutocompleteOption) => email.value),
      notes,
      auto_renew: autoRenew === 'ongoing_subscription',
    };
    await createGiftSubscription(sanitizedParams);
    formik.resetForm();
    if (forGiftLandingPage) {
      setCurrentFormStep(1);
      setNumberOfGifts(totalEligibleSubs);
    }
  };

  const prepareConfirmation = async ({ emails }: { emails: AutocompleteOption[] }) => {
    const { totalCostWithoutTax, totalCostWithTax, totalTaxAmount, totalEligibleSubs } =
      await confirmGroupSubscription({
        emails: emails.map((email) => email.value),
        group_subscription_type: GROUP_SUBSCRIPTION_GIFT,
      });
    setTotalCostWithoutTax(totalCostWithoutTax);
    setTotalCostWithTax(totalCostWithTax);
    setTotalTaxAmount(totalTaxAmount);
    setTotalEligibleSubs(totalEligibleSubs);
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={INITIAL_STATE}
        onSubmit={confirmSubmit}
        validationSchema={schema}
        validateOnBlur={false}
      >
        {(formik) => (
          <Form noValidate onSubmit={formik.handleSubmit} className={styles.formContainer}>
            <TokenizeTextField
              className="mb-24"
              name="emails"
              label={
                forGiftLandingPage
                  ? 'Add people by email, separating each address by a comma.'
                  : 'Emails'
              }
              onChangeCallBack={() => prepareConfirmation(formik.values)}
            />
            <TextAreaField
              className="mb-24"
              name="notes"
              autoComplete="off"
              placeholder="Add an optional note which will be sent to gift recipients with their login information."
              rows={6}
            />
            <RadioButtonGroup name="autoRenew" options={AUTO_RENEW_OPTIONS} />
            {totalEligibleSubs > 0 && formik.isValid && formik.dirty && (
              <>
                <Divider marginY="medium" />
                <div className={styles.totalContainer}>
                  <div>Accounts total: {totalEligibleSubs}</div>
                </div>
                <PriceBreakDown
                  totalCostWithoutTax={totalCostWithoutTax}
                  totalCostWithTax={totalCostWithTax}
                  totalTaxAmount={totalTaxAmount}
                  isLoadingConfirmGroupSubscription={isLoadingForPriceConfirmation}
                />
                {formik.values.autoRenew === 'ongoing_subscription' && (
                  <span className={styles.accountSummaryNote}>
                    After initial year, subscription will renew at base subscription pricing
                  </span>
                )}
                <Divider marginY="medium" />
              </>
            )}
            <PrimaryButton
              as="button"
              className={clsx('!mt-24', {
                [styles.purchaseBtnLanding]: forGiftLandingPage,
              })}
              type="submit"
              disabled={!(formik.isValid && formik.dirty) || isLoadingPurchase}
              isLoading={isLoadingPurchase}
              fullWidth
            >
              Purchase
            </PrimaryButton>
          </Form>
        )}
      </Formik>
      <ConfirmActionViaEmailCode
        actionText="Gift Subscription"
        isOpen={showConfirmationModal}
        setIsOpen={setShowConfirmationModal}
        setIsConfirmed={setIsUserConfirmed}
      />
    </>
  );
};

export default GiftForm;
